

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface UserProfile {
  id: string;
  auth_user_id: string;
  name: string;
  email: string;
  phone_number?: string;
  country?: string | null;
  last_login?: string;
  organization_id?: string;
  organization_name?: string;
  created_at: string;
  updated_at: string;
}

export function useUserProfile() {
  return useQuery({
    queryKey: ['user-profile'],
    queryFn: async (): Promise<UserProfile | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // 1. Query by auth_user_id
      let { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile by auth_user_id:', error);
      }

      // 2. Fallback: check if row exists by email and link auth_user_id
      if (!data && user.email) {
        const { data: emailData, error: emailError } = await supabase
          .from('users')
          .select('*')
          .eq('email', user.email)
          .maybeSingle();

        if (emailError) {
          console.error('Error fetching user profile by email:', emailError);
        } else if (emailData) {
          data = emailData;
          if (emailData.auth_user_id !== user.id) {
            void supabase
              .from('users')
              .update({ auth_user_id: user.id })
              .eq('id', emailData.id);
          }
        }
      }

      // 3. Fallback: user metadata from Supabase Auth
      const meta = (user.user_metadata || {}) as Record<string, any>;
      const fallbackName = (
        meta.first_name || meta.last_name
          ? `${meta.first_name || ''} ${meta.last_name || ''}`.trim()
          : (meta.name as string) || (meta.full_name as string) || ''
      );

      let organization_name: string | null = null;
      const orgId = data?.organization_id || (meta.organization_id as string) || null;
      if (orgId) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', orgId)
          .maybeSingle();
        if (org) {
          organization_name = org.name;
        }
      }

      if (!data) {
        // Return fallback profile so the user can see their details and add name/phone smoothly
        return {
          id: user.id,
          auth_user_id: user.id,
          name: fallbackName,
          email: user.email || '',
          phone_number: (meta.phone as string) || (meta.phone_number as string) || '',
          country: (meta.country as string) ?? null,
          organization_id: orgId || undefined,
          organization_name: organization_name || undefined,
          created_at: user.created_at,
          updated_at: user.updated_at || user.created_at,
        };
      }

      return {
        ...data,
        name: data.name || fallbackName,
        phone_number: data.phone || (meta.phone as string) || (meta.phone_number as string) || '',
        country: data.country ?? (meta.country as string) ?? null,
        organization_name: organization_name || undefined,
      } as UserProfile;
    },
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData: Partial<UserProfile>) => {
      console.log('[useUpdateUserProfile] incoming data:', profileData);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('[useUpdateUserProfile] auth.getUser error:', authError);
      }
      if (!user) {
        throw new Error('User not authenticated');
      }

      const updates: { name?: string; phone?: string } = {};
      if (typeof profileData.name === 'string') updates.name = profileData.name.trim();
      if (typeof profileData.phone_number === 'string') updates.phone = profileData.phone_number.trim();

      if (Object.keys(updates).length === 0) {
        throw new Error('No valid fields to update');
      }

      // Step 1: Sync auth metadata in Supabase Auth
      try {
        const nameParts = (updates.name || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        await supabase.auth.updateUser({
          data: {
            ...(updates.name !== undefined ? { name: updates.name, first_name: firstName, last_name: lastName } : {}),
            ...(updates.phone !== undefined ? { phone: updates.phone } : {}),
          },
        });
      } catch (authErr) {
        console.warn('[useUpdateUserProfile] auth metadata update warning:', authErr);
      }

      // Step 2: Try RPC function if available (runs SECURITY DEFINER)
      try {
        const { data: rpcResult, error: rpcError } = await (supabase as any).rpc(
          'update_or_create_user_profile',
          {
            p_name: updates.name ?? null,
            p_phone: updates.phone ?? null,
          }
        );

        if (!rpcError && rpcResult) {
          console.log('[useUpdateUserProfile] RPC update success:', rpcResult);
          return rpcResult;
        }
      } catch (rpcErr) {
        console.warn('[useUpdateUserProfile] RPC attempt failed, falling back to direct table update:', rpcErr);
      }

      // Step 3: Direct update by auth_user_id
      const { data: updatedData, error: updateError } = await supabase
        .from('users')
        .update(updates)
        .eq('auth_user_id', user.id)
        .select('id, name, phone, email, organization_id, updated_at')
        .maybeSingle();

      if (updateError) {
        console.error('[useUpdateUserProfile] Supabase update error:', updateError);
        throw new Error(updateError.message || 'Unknown error from Supabase');
      }

      if (updatedData) {
        console.log('[useUpdateUserProfile] update success:', updatedData);
        return updatedData;
      }

      // Step 4: If no row updated by auth_user_id, check if record exists by email
      if (user.email) {
        const { data: emailRow } = await supabase
          .from('users')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();

        if (emailRow) {
          const { data: linkedData, error: linkErr } = await supabase
            .from('users')
            .update({
              ...updates,
              auth_user_id: user.id,
            })
            .eq('id', emailRow.id)
            .select('id, name, phone, email, organization_id, updated_at')
            .maybeSingle();

          if (!linkErr && linkedData) {
            console.log('[useUpdateUserProfile] email link & update success:', linkedData);
            return linkedData;
          }
        }
      }

      // Step 5: If row still does not exist, insert a new record into public.users
      const meta = (user.user_metadata || {}) as Record<string, any>;
      const { data: insertedData, error: insertError } = await supabase
        .from('users')
        .insert({
          auth_user_id: user.id,
          email: user.email || '',
          name: updates.name || (meta.first_name as string) || user.email?.split('@')[0] || 'User',
          phone: updates.phone || null,
          country: (meta.country as string) ?? null,
          status: 'active',
        })
        .select('id, name, phone, email, organization_id, updated_at')
        .maybeSingle();

      if (insertError) {
        console.error('[useUpdateUserProfile] Insert error:', insertError);
        throw new Error(insertError.message || 'Failed to create user profile in database.');
      }

      if (insertedData) {
        console.log('[useUpdateUserProfile] insert success:', insertedData);
        return insertedData;
      }

      // Synthetic fallback return
      return {
        id: user.id,
        auth_user_id: user.id,
        name: updates.name || '',
        phone: updates.phone || '',
        email: user.email || '',
        updated_at: new Date().toISOString(),
      };
    },
    onSuccess: () => {
      console.log('[useUpdateUserProfile] invalidating user-profile query');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast({
        title: "Profile updated successfully",
        description: "Your profile information has been saved.",
      });
    },
    onError: (error: any) => {
      const description = error?.message || 'Failed to update your profile. Please try again.';
      console.error('[useUpdateUserProfile] Profile update failed:', error);
      toast({
        title: "Error updating profile",
        description,
        variant: "destructive",
      });
    },
  });
}

